'use client'

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react'
import { getTenantFromLocalhost, isDevelopmentMode, switchDevTenant, resetDevTenant, getDevTenantInfo } from '@/lib/dev-tenant-config'
import { useAuthStore } from '@/stores/auth-store';
import { useTenantStore, type Tenant } from '@/stores/tenant-store'

export interface TenantContextType {
  tenant: Tenant | null
  loading: boolean
  error: string | null
  isInitialized: boolean
  detectTenant: () => Promise<Tenant | null>
  setTenant: (tenant: Tenant | null) => void
  getTenantSlug: () => string | null
  getTenantDomain: () => string | null
  isCustomDomain: () => boolean
  getApiBaseUrl: () => string
  // Development methods
  switchTenant: (tenantSlug: string) => void
  resetTenant: () => void
  isDevMode: boolean
  devTenantInfo: any
}

const TenantContext = createContext<TenantContextType | undefined>(undefined)

export const useTenant = () => {
  const context = useContext(TenantContext)
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider')
  }
  return context
}

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mounted, setMounted] = React.useState(false)
  const isDevMode = isDevelopmentMode()
  
  // Use Zustand store for tenant state
  const {
    tenant,
    loading,
    error,
    isInitialized,
    setTenant,
    setLoading,
    setError,
    setInitialized
  } = useTenantStore()
  
  // Get auth state to listen for authentication changes
  const { isAuthenticated, isInitialized: authInitialized } = useAuthStore()
  
  const [devTenantInfo, setDevTenantInfo] = useState<any>(null)

  // Set mounted state
  useEffect(() => {
    setMounted(true)
  }, [])

  // Extract tenant slug from subdomain
  const getTenantSlug = useCallback((): string | null => {
    if (typeof window === 'undefined') return null
    
    const host = window.location.hostname
    const parts = host.split('.')
    
    // Handle localhost development
    if (host === 'localhost' || host.includes('localhost')) {
      return getTenantFromLocalhost()
    }
    
    // Handle custom domains (e.g., mycompany.com)
    if (parts.length === 2) {
      return null // Custom domain, will be detected via API
    }
    
    // Handle subdomains (e.g., tenant1.yourdomain.com)
    if (parts.length >= 3) {
      return parts[0]
    }
    
    return null
  }, [])

  // Check if using custom domain
  const isCustomDomain = useCallback((): boolean => {
    if (typeof window === 'undefined') return false
    
    const host = window.location.hostname
    const parts = host.split('.')
    
    // Custom domain has only 2 parts (e.g., mycompany.com)
    return parts.length === 2 && !host.includes('localhost')
  }, [])

  // Get tenant domain
  const getTenantDomain = useCallback((): string | null => {
    if (typeof window === 'undefined') return null
    return window.location.hostname
  }, [])

  // Get API base URL with tenant context
  const getApiBaseUrl = useCallback((): string => {
    const baseUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000'
    return baseUrl
  }, [])

  // Detect tenant from current domain/subdomain
  const detectTenant = useCallback(async (): Promise<Tenant | null> => {
    try {
      let slug = getTenantSlug()
      const domain = getTenantDomain()
      const baseUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000'
      const host = window.location.hostname
      
      console.log('🔍 Detecting tenant...', { slug, domain })
      
      // Try to get user's tenant info if authenticated (works in both dev and production)
      try {
        const accessToken = useAuthStore.getState().accessToken;
        console.log('Access token exists:', !!accessToken)
        if (accessToken) {
          console.log('🔍 Attempting to get user tenant info...')
          
          const userResponse = await fetch(`${baseUrl}/api/user/tenant-info/`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
              // Only add X-Tenant header in development
              ...(host === 'localhost' || host.includes('localhost') ? { 'X-Tenant': slug || 'default' } : {}),
            },
          })
          
          console.log('User API response status:', userResponse.status)
          
          if (userResponse.ok) {
            const userData = await userResponse.json()
            console.log('User data received:', userData)
            
            if (userData.tenant) {
              console.log('✅ Tenant found from user data:', userData.tenant)
              return userData.tenant
            }
          }
        } else {
          console.log('No access token found, skipping user tenant info call')
        }
      } catch (error) {
        console.error('Failed to get user tenant info:', error)
      }

      // Try tenant detection API
      const response = await fetch(`${baseUrl}/api/tenants/detect/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          domain: domain,
          slug: slug,
          is_custom_domain: isCustomDomain(),
        }),
      })

      if (!response.ok) {
        console.error('Tenant detection API failed:', response.status)
        return null
      }

      const responseData = await response.json()
      console.log('Detection API response:', responseData)
      return responseData.tenant
    } catch (error) {
      console.error('❌ Tenant detection failed:', error)
      return null
    }
  }, [getTenantSlug, getTenantDomain, isCustomDomain])

  // Initialize tenant detection
  useEffect(() => {
    if (!mounted) return; // Don't run during SSR
    
    const initializeTenant = async () => {
      try {
        setLoading(true)
        setError(null)
        
        // Check if we're on an auth page
        const pathname = window.location.pathname
        const authPaths = ['/login', '/signup', '/forgot-password', '/reset-password']
        const isAuthPage = authPaths.some(path => pathname.startsWith(path))
        
        if (isAuthPage) {
          console.log('On auth page, skipping tenant detection')
          setLoading(false)
          setInitialized(true)
          return
        }
        
        // Check if user is authenticated by checking for access token
        const accessToken = useAuthStore.getState().accessToken;
        if (!accessToken) {
          console.log('No access token found, skipping tenant detection')
          setLoading(false)
          setInitialized(true)
          return
        }
        
        const detectedTenant = await detectTenant()
        setTenant(detectedTenant)
        
        // Set dev tenant info if in development mode
        if (isDevMode && detectedTenant) {
          const devInfo = getDevTenantInfo(detectedTenant.slug)
          setDevTenantInfo(devInfo)
        }
        
        if (!detectedTenant) {
          // Handle no tenant detected (could be main domain or invalid subdomain)
          console.log('No tenant detected')
        }
      } catch (error) {
        console.error('Tenant initialization failed:', error)
        setError('Failed to initialize tenant')
      } finally {
        setLoading(false)
        setInitialized(true)
      }
    }

    initializeTenant()
  }, [mounted, detectTenant, isDevMode, setTenant, setLoading, setError, setInitialized])

  // Listen for authentication changes and re-detect tenant
  useEffect(() => {
    if (!mounted) return; // Don't run during SSR
    
    const handleAuthChange = async () => {
      // Only trigger if auth is initialized and user is authenticated
      if (authInitialized && isAuthenticated) {
        console.log('🔄 Auth state changed, re-detecting tenant...')
        
        // Check if we're on an auth page
        const pathname = window.location.pathname
        const authPaths = ['/login', '/signup', '/forgot-password', '/reset-password']
        const isAuthPage = authPaths.some(path => pathname.startsWith(path))
        
        if (isAuthPage) {
          console.log('On auth page, skipping tenant re-detection')
          return
        }
        
        try {
          setLoading(true)
          setError(null)
          
          const detectedTenant = await detectTenant()
          setTenant(detectedTenant)
          
          // Set dev tenant info if in development mode
          if (isDevMode && detectedTenant) {
            const devInfo = getDevTenantInfo(detectedTenant.slug)
            setDevTenantInfo(devInfo)
          }
          
          if (!detectedTenant) {
            console.log('No tenant detected after auth change')
          }
        } catch (error) {
          console.error('Tenant re-detection failed:', error)
          setError('Failed to re-detect tenant')
        } finally {
          setLoading(false)
        }
      }
    }

    handleAuthChange()
  }, [mounted, isAuthenticated, authInitialized, detectTenant, isDevMode, setTenant, setLoading, setError])

  // Development tenant switching
  const handleSwitchTenant = useCallback((tenantSlug: string) => {
    if (isDevMode) {
      switchDevTenant(tenantSlug)
    }
  }, [isDevMode])

  const handleResetTenant = useCallback(() => {
    if (isDevMode) {
      resetDevTenant()
    }
  }, [isDevMode])

  const contextValue: TenantContextType = {
    tenant,
    loading,
    error,
    isInitialized,
    detectTenant,
    setTenant,
    getTenantSlug,
    getTenantDomain,
    isCustomDomain,
    getApiBaseUrl,
    switchTenant: handleSwitchTenant,
    resetTenant: handleResetTenant,
    isDevMode,
    devTenantInfo,
  }

  // Don't render the provider during SSR
  if (typeof window === 'undefined') {
    return <>{children}</>
  }

  return (
    <TenantContext.Provider value={contextValue}>
      {children}
    </TenantContext.Provider>
  )
} 